import type { Principal, UUID } from "./sync/types";

// lookupBatch is how many people one lookup asks the server for: 100 IDs
// keep its URL under 4 KB, well inside what proxies take.
const lookupBatch = 100;
// retryAfter is how long someone whose lookup failed waits to be asked again.
const retryAfter = 10_000;
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

// Directory is the people and agents the client knows. It starts with
// everyone active; anyone else, such as someone who has left, is looked up
// the first time get() asks for them, with the others asked for at the same
// time, and arrived() is called once they are in.
export class Directory extends Map<UUID, Principal> {
  private asked = new Set<UUID>();
  private wanted: UUID[] = [];

  constructor(
    people: Iterable<Principal>,
    private lookup: (ids: UUID[]) => Promise<Principal[]>,
    private arrived: () => void,
  ) {
    super();
    for (const p of people) this.set(p.id, p);
  }

  override get(id: UUID): Principal | undefined {
    const p = super.get(id);
    if (!p && uuid.test(id) && !this.asked.has(id)) this.want(id);
    return p;
  }

  private want(id: UUID) {
    this.asked.add(id);
    this.wanted.push(id);
    if (this.wanted.length === 1) queueMicrotask(() => this.flush());
  }

  private flush() {
    const ids = this.wanted;
    this.wanted = [];
    for (let i = 0; i < ids.length; i += lookupBatch) {
      const batch = ids.slice(i, i + lookupBatch);
      this.lookup(batch).then(
        (found) => {
          for (const p of found) this.set(p.id, p);
          if (found.length > 0) this.arrived();
        },
        () => setTimeout(() => batch.forEach((id) => this.asked.delete(id)), retryAfter),
      );
    }
  }
}
